import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import PrimaryButton from "../components/PrimaryButton";
import {
  DEFAULT_SCORECARD_SEASON_YEARS,
  SCORECARD_LIVE_SEASON_YEAR,
  extractAvailableYears,
  extractScorecardData,
  formatSeasonYearOptionLabel,
  getComparisonYearFromScorecardData,
  getPreviousYearColumnLabel,
  buildScorecardExportQueryParams,
  canFilterScorecardByTannery,
  fetchScorecardTanneryFilterOptions,
  getScorecardEndpoint,
  getScorecardErrorMessage,
  getScorecardExportEndpoint,
  getScorecardQueryString,
  isLiveSeasonYear,
  isTanneryWiseExport,
  mergeSeasonYearOptions,
  mergeTanneryFilterOptions,
} from "../utils/scorecardApi";

const AUTH_TOKEN_STORAGE_KEY = "authToken";
const AUTH_ROLE_STORAGE_KEY = "authRole";
const EID_DAY_OPTIONS = [
  { label: "All Days", value: "all" },
  { label: "Eid Day 1", value: "EID_DAY_1" },
  { label: "Eid Day 2", value: "EID_DAY_2" },
  { label: "Eid Day 3", value: "EID_DAY_3" },
  { label: "Eid Day 4", value: "EID_DAY_4" },
];
const SORTABLE_SCORE_COLUMNS = [
  "total",
  "cow",
  "goat",
  "sheep",
  "camel",
  "buffalo",
  "previous_total",
  "current_total",
  "diff",
  "percent",
  "remaining",
];

const normalizeRole = (role) => {
  const cleaned = String(role || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]/g, "_");

  if (cleaned === "SUPERADMIN") {
    return "SUPER_ADMIN";
  }
  if (cleaned === "TANNERYADMIN") {
    return "TANNERY_ADMIN";
  }

  return cleaned;
};

const getBaseApiUrl = () => {
  const baseApiUrl = process.env.REACT_APP_BASE_API_URL;
  if (!baseApiUrl) {
    throw new Error("REACT_APP_BASE_API_URL is not configured.");
  }
  return baseApiUrl.replace(/\/+$/, "");
};

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const getCount = (row, keys) => {
  for (const key of keys) {
    if (row && row[key] != null) {
      return toNumber(row[key]);
    }
  }
  return 0;
};

const normalizeCountRow = (row, labelKey = "total") => ({
  ...((row?.counts && typeof row.counts === "object" ? row.counts : {})),
  ...(row && typeof row === "object" ? row : {}),
  label:
    row?.label ||
    row?.name ||
    row?.tannery_name ||
    row?.tanneryName ||
    row?.zila ||
    row?.zila_name ||
    row?.district ||
    row?.elaqa ||
    row?.elaqa_name ||
    row?.area ||
    labelKey,
  total: getCount(row, ["total_count", "total", "count"]),
  cow: getCount(row, ["cow_count", "cow"]),
  goat: getCount(row, ["goat_count", "goat"]),
  sheep: getCount(row, ["sheep_count", "sheep"]),
  camel: getCount(row, ["camel_count", "camel"]),
  buffalo: getCount(row, ["buffalo_count", "buffalo"]),
  previous_year: toNumber(row?.previous_year ?? row?.prev_year),
  previous_total: getCount(row, [
    "previous_total",
    "prev_year",
    "previous_year",
    "prev_year_total",
  ]),
  current_total: getCount(row, ["current_total"]),
  // Some API responses provide only `previous_total` + `current_total` (no `diff` / `percent`).
  // Compute them to keep the table consistent.
  diff:
    row?.diff != null
      ? getCount(row, ["diff"])
      : getCount(row, ["current_total"]) - getCount(row, ["previous_total"]),
  percent:
    row?.percent != null
      ? getCount(row, ["percent"])
      : (() => {
          const prev = getCount(row, ["previous_total"]);
          const curr = getCount(row, ["current_total"]);
          if (!prev) return 0;
          return (curr / prev) * 100;
        })(),
  remaining: getCount(row, ["remaining"]),
  has_surpassed: Boolean(row?.has_surpassed),
});

const formatPercentValue = (value) => `${Number(value || 0).toFixed(2)}%`;

const getPrevYearValue = (row) => {
  const fromTotal = row?.previous_total;
  if (fromTotal != null && fromTotal !== "") {
    return toNumber(fromTotal);
  }
  return toNumber(row?.previous_year);
};

const BASE_SCORE_METRIC_COLUMNS = [
  { key: "total", label: "Total" },
  { key: "cow", label: "Cow" },
  { key: "goat", label: "Goat" },
  { key: "sheep", label: "Sheep" },
  { key: "camel", label: "Camel" },
  { key: "buffalo", label: "Buffalo" },
  { key: "previous_total", label: "Prev Year", getValue: getPrevYearValue },
  { key: "diff", label: "Diff" },
  { key: "percent", label: "Achieved %", format: (row) => formatPercentValue(row.percent) },
];

const buildScoreMetricColumns = (comparisonYear) =>
  BASE_SCORE_METRIC_COLUMNS.map((column) =>
    column.key === "previous_total"
      ? { ...column, label: getPreviousYearColumnLabel(comparisonYear) }
      : column
  );

const SCORE_COMPARISON_COLUMN_KEYS = new Set(["previous_total", "diff", "percent"]);

const ScoreColGroup = ({ metricColumns }) => (
  <colgroup>
    <col className="score-col-label" />
    {metricColumns.map((column) => (
      <col key={column.key} className="score-col-metric" />
    ))}
  </colgroup>
);

const hasCountFields = (section) => {
  if (!section || typeof section !== "object" || Array.isArray(section)) {
    return false;
  }

  return [
    "total_count",
    "cow_count",
    "goat_count",
    "sheep_count",
    "camel_count",
    "buffalo_count",
  ].some((key) => Object.prototype.hasOwnProperty.call(section, key));
};

const toRows = (section, fallbackLabel) => {
  if (Array.isArray(section)) {
    return section.map((item) => {
      const comparison =
        item && typeof item === "object" && item.comparison && typeof item.comparison === "object"
          ? item.comparison
          : {};
      const mergedRow =
        item && typeof item === "object" && item.counts && typeof item.counts === "object"
          ? { ...item.counts, ...item, ...comparison }
          : item;
      return normalizeCountRow(mergedRow, fallbackLabel);
    });
  }
  if (section && typeof section === "object") {
    if (hasCountFields(section)) {
      return [normalizeCountRow(section, fallbackLabel)];
    }

    return Object.entries(section).map(([key, value]) =>
      normalizeCountRow(
        {
          ...(typeof value === "object" && value !== null ? value : { total: value }),
          label: key,
        },
        fallbackLabel
      )
    );
  }
  return [];
};

const getTanneryId = (row) => String(row?.tannery_id ?? row?.tanneryId ?? "").trim();

const getMetricCellValue = (column, row) => {
  if (column.format) {
    return column.format(row);
  }
  if (column.getValue) {
    return column.getValue(row);
  }
  return row[column.key] ?? 0;
};

function ScoreMetricHeader({
  metricColumns,
  tableKey,
  enableSort,
  renderSortLabel,
  onToggleSort,
  showComparisonColumns = true,
}) {
  return metricColumns.map((column) => {
    if (!showComparisonColumns && SCORE_COMPARISON_COLUMN_KEYS.has(column.key)) {
      return <th key={column.key} scope="col" aria-hidden="true"></th>;
    }

    return (
      <th key={column.key} scope="col">
        {enableSort && SORTABLE_SCORE_COLUMNS.includes(column.key) ? (
          <button
            type="button"
            className="score-sort-btn"
            onClick={() => onToggleSort(tableKey, column.key)}
          >
            {renderSortLabel(tableKey, column.key, column.label)}
          </button>
        ) : (
          column.label
        )}
      </th>
    );
  });
}

function ScoreMetricCells({ metricColumns, row, showComparisonColumns = true }) {
  return metricColumns.map((column) => {
    if (!showComparisonColumns && SCORE_COMPARISON_COLUMN_KEYS.has(column.key)) {
      return <td key={column.key} aria-hidden="true"></td>;
    }

    return <td key={column.key}>{getMetricCellValue(column, row)}</td>;
  });
}

function ScoreDataTable({
  metricColumns,
  tableKey,
  rows,
  emptyMessage,
  labelHeader,
  getRowLabel,
  enableSort = true,
  showComparisonColumns = true,
  renderSortLabel,
  onToggleSort,
}) {
  const tableColCount = 1 + metricColumns.length;

  return (
    <div className="dashboard-users-table-wrapper">
      <table className="dashboard-users-table score-table">
        <ScoreColGroup metricColumns={metricColumns} />
        <thead>
          <tr>
            <th scope="col">{labelHeader}</th>
            <ScoreMetricHeader
              metricColumns={metricColumns}
              tableKey={tableKey}
              enableSort={enableSort}
              showComparisonColumns={showComparisonColumns}
              renderSortLabel={renderSortLabel}
              onToggleSort={onToggleSort}
            />
          </tr>
        </thead>
        <tbody>
          {rows.length > 0 ? (
            rows.map((row, index) => (
              <tr key={`${tableKey}-${index}`}>
                <td>{getRowLabel(row, index)}</td>
                <ScoreMetricCells
                  metricColumns={metricColumns}
                  row={row}
                  showComparisonColumns={showComparisonColumns}
                />
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={tableColCount}>{emptyMessage}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ScorePage() {
  const navigate = useNavigate();
  const userRole = normalizeRole(localStorage.getItem(AUTH_ROLE_STORAGE_KEY));
  const isSuperAdmin = userRole === "SUPER_ADMIN";
  const isTanneryAdmin = userRole === "TANNERY_ADMIN";
  const isDataEntry = userRole === "DATA_ENTRY" || userRole === "DATAENTRY";
  const isExecutive = userRole === "EXECUTIVE";
  const isAllowedRole = isSuperAdmin || isTanneryAdmin || isDataEntry || isExecutive;
  const canUseTanneryFilter = canFilterScorecardByTannery(userRole);

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedSeasonYear, setSelectedSeasonYear] = useState(String(SCORECARD_LIVE_SEASON_YEAR));
  const [seasonYearOptions, setSeasonYearOptions] = useState(() => [...DEFAULT_SCORECARD_SEASON_YEARS]);
  const [activeComparisonYear, setActiveComparisonYear] = useState(null);
  const [selectedEidDay, setSelectedEidDay] = useState("all");
  const [selectedTanneryId, setSelectedTanneryId] = useState("all");
  const [tanneryFilterOptions, setTanneryFilterOptions] = useState([
    { value: "all", label: "All Tanneries" },
  ]);
  const [exportingKey, setExportingKey] = useState("");
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportConfig, setExportConfig] = useState({
    eid_day: "all",
    tannery_id: "all",
    language: "en",
    format: "csv",
  });
  const [overallRows, setOverallRows] = useState([]);
  const [tanneryRows, setTanneryRows] = useState([]);
  const [zilaRows, setZilaRows] = useState([]);
  const [elaqaRows, setElaqaRows] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    overall: { key: null, direction: "asc" },
    tannery: { key: null, direction: "asc" },
    zila: { key: null, direction: "asc" },
    elaqa: { key: null, direction: "asc" },
  });

  const parsePayload = async (response) => {
    const text = await response.text();
    if (!text) {
      return null;
    }
    try {
      return JSON.parse(text);
    } catch (error) {
      return text;
    }
  };

  const triggerDownload = (blob, fileName) => {
    const objectUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(objectUrl);
  };

  const handleExport = async (config) => {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    const { format, language, eid_day, tannery_id } = config;
    const useTanneryWise = isTanneryWiseExport(tannery_id);
    const params = buildScorecardExportQueryParams({
      format,
      language,
      eidDay: eid_day,
      tanneryId: tannery_id,
    });
    const exportEidDay = params.get("eid_day") || "all";
    const actionKey = `${format}-${language}-${useTanneryWise ? tannery_id : "zila"}`;
    const exportKind = useTanneryWise ? "tannery-wise" : "zila-wise";
    try {
      setExportingKey(actionKey);
      setErrorMessage("");
      const response = await fetch(
        `${getBaseApiUrl()}${getScorecardExportEndpoint(tannery_id)}?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const payload = await parsePayload(response);
        throw new Error(
          (payload && typeof payload === "object" && (payload.message || payload.detail)) ||
            `Unable to export ${exportKind} score right now.`
        );
      }

      const blob = await response.blob();
      const extension = format === "pdf" ? "pdf" : "csv";
      const tannerySuffix = useTanneryWise ? `-tannery-${tannery_id}` : "";
      const fallbackName = `${exportKind}-score-${language}-${exportEidDay}${tannerySuffix}.${extension}`;
      const disposition = response.headers.get("content-disposition") || "";
      const matchedFileName = disposition.match(/filename="?([^"]+)"?/i)?.[1];
      triggerDownload(blob, matchedFileName || fallbackName);
      setIsExportModalOpen(false);
    } catch (error) {
      setErrorMessage(error.message || `Unable to export ${exportKind} score right now.`);
    } finally {
      setExportingKey("");
    }
  };

  const openExportModal = () => {
    const exportTanneryId =
      isLiveSeasonYear(selectedSeasonYear) && selectedTanneryId !== "all"
        ? selectedTanneryId
        : "all";
    setExportConfig((prev) => ({
      ...prev,
      eid_day: selectedEidDay || "all",
      tannery_id: exportTanneryId,
    }));
    setIsExportModalOpen(true);
  };

  const loadTanneryFilterOptions = useCallback(async () => {
    if (!canUseTanneryFilter) {
      return;
    }

    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    try {
      const options = await fetchScorecardTanneryFilterOptions(getBaseApiUrl(), token, userRole);
      if (!options?.length) {
        return;
      }
      setTanneryFilterOptions((prev) => mergeTanneryFilterOptions(prev, options));
    } catch {
      // Keep options from scorecard tannery_wise when the list endpoint fails.
    }
  }, [canUseTanneryFilter, navigate, userRole]);

  const loadScore = useCallback(async () => {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage("");

      const queryString = getScorecardQueryString({
        year: selectedSeasonYear,
        tanneryId: selectedTanneryId,
        eidDay: selectedEidDay,
        userRole,
      });
      const response = await fetch(
        `${getBaseApiUrl()}${getScorecardEndpoint(userRole, { tanneryId: selectedTanneryId })}${queryString}`,
        {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const payload = await parsePayload(response);
      if (!response.ok) {
        const availableYears = extractAvailableYears(payload);
        if (availableYears.length > 0) {
          setSeasonYearOptions((prev) => mergeSeasonYearOptions(prev, availableYears));
        }
        throw new Error(getScorecardErrorMessage(payload, "Unable to load score data right now."));
      }

      const data = extractScorecardData(payload);
      const comparisonYear = getComparisonYearFromScorecardData(data);
      setActiveComparisonYear(comparisonYear);
      setSeasonYearOptions((prev) =>
        mergeSeasonYearOptions(prev, extractAvailableYears(payload), DEFAULT_SCORECARD_SEASON_YEARS)
      );

      const overallSection = data?.summary || data?.overall || data?.totals || data?.total || data;
      const overallComparison =
        data?.total_comparison && typeof data.total_comparison === "object"
          ? data.total_comparison
          : {};
      const nextOverallRows = toRows(overallSection, "Total");
      const nextTanneryRows = toRows(data?.tannery_wise || data?.tanneryWise, "Tannery");
      const nextZilaRows = toRows(data?.zila_wise || data?.zilaWise, "Zila");
      const nextElaqaRows = toRows(data?.elaqa_wise || data?.elaqaWise, "Elaqa");

      const mergedOverallRows =
        nextOverallRows.length > 0
          ? nextOverallRows.map((row) => normalizeCountRow({ ...row, ...overallComparison }, "Total"))
          : [normalizeCountRow({ ...overallComparison }, "Total")];
      setOverallRows(mergedOverallRows);
      setTanneryRows(nextTanneryRows);
      setZilaRows(nextZilaRows);
      setElaqaRows(nextElaqaRows);

      // Build tannery filter options from returned rows when IDs are available.
      const dynamicOptions = nextTanneryRows
        .map((row) => {
          const tanneryId = getTanneryId(row);
          const tanneryLabel = String(row?.tannery_name || row?.tanneryName || row?.label || "").trim();
          if (!tanneryId || !tanneryLabel) {
            return null;
          }
          return { value: tanneryId, label: tanneryLabel };
        })
        .filter(Boolean);

      const dedupedOptions = Array.from(
        new Map(dynamicOptions.map((item) => [item.value, item])).values()
      );
      if (canUseTanneryFilter) {
        setTanneryFilterOptions((prevOptions) => mergeTanneryFilterOptions(prevOptions, dedupedOptions));
      }
    } catch (error) {
      setErrorMessage(error.message || "Unable to load score data right now.");
    } finally {
      setIsLoading(false);
    }
  }, [canUseTanneryFilter, navigate, selectedEidDay, selectedSeasonYear, selectedTanneryId, userRole]);

  const metricColumns = useMemo(
    () => buildScoreMetricColumns(activeComparisonYear),
    [activeComparisonYear]
  );

  const isLiveSeason = isLiveSeasonYear(selectedSeasonYear);
  const showTanneryFilter = isLiveSeason && canUseTanneryFilter;
  const showTanneryWiseTable = showTanneryFilter && selectedTanneryId === "all";
  const showElaqaBreakdown = isLiveSeason;
  // Zila Wise always shows YoY columns; Tannery Wise (all tanneries) hides them.
  const showZilaComparisons = true;

  const seasonYearSelectOptions = useMemo(
    () => mergeSeasonYearOptions(seasonYearOptions, selectedSeasonYear),
    [seasonYearOptions, selectedSeasonYear]
  );

  const handleSeasonYearChange = (event) => {
    const nextYear = event.target.value;
    setSelectedSeasonYear(nextYear);
    if (!isLiveSeasonYear(nextYear)) {
      setSelectedTanneryId("all");
    }
  };

  useEffect(() => {
    if (!isAllowedRole) {
      return;
    }
    void loadTanneryFilterOptions();
    loadScore();
  }, [isAllowedRole, loadScore, loadTanneryFilterOptions]);

  const toggleSort = (tableKey, columnKey) => {
    if (!SORTABLE_SCORE_COLUMNS.includes(columnKey)) {
      return;
    }

    setSortConfig((prev) => {
      const current = prev[tableKey] || { key: null, direction: "asc" };
      const nextDirection =
        current.key === columnKey && current.direction === "asc" ? "desc" : "asc";

      return {
        ...prev,
        [tableKey]: { key: columnKey, direction: nextDirection },
      };
    });
  };

  const sortRows = (rows, tableKey) => {
    const config = sortConfig[tableKey];
    if (!config?.key) {
      return rows;
    }

    const sortedRows = [...rows].sort((a, b) => {
      const aValue = Number(a?.[config.key] ?? 0);
      const bValue = Number(b?.[config.key] ?? 0);
      return config.direction === "asc" ? aValue - bValue : bValue - aValue;
    });

    return sortedRows;
  };

  const sortedOverallRows = useMemo(
    () => sortRows(overallRows, "overall"),
    [overallRows, sortConfig]
  );
  const sortedTanneryRows = useMemo(
    () => sortRows(tanneryRows, "tannery"),
    [tanneryRows, sortConfig]
  );
  const sortedZilaRows = useMemo(() => sortRows(zilaRows, "zila"), [zilaRows, sortConfig]);
  const sortedElaqaRows = useMemo(
    () => sortRows(elaqaRows, "elaqa"),
    [elaqaRows, sortConfig]
  );

  const renderSortLabel = (tableKey, columnKey, label) => {
    const current = sortConfig[tableKey];
    const isActive = current?.key === columnKey;
    const indicator = isActive ? (current.direction === "asc" ? " ▲" : " ▼") : " ⇅";
    return `${label}${indicator}`;
  };

  if (!isAllowedRole) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <section className="dashboard-card">
      <div className="dashboard-users-list-header">
        <h2>Score</h2>
        <div className="score-header-actions">
          <label htmlFor="score-season-year-filter" className="score-filter-label">
            Year
          </label>
          <select
            id="score-season-year-filter"
            value={selectedSeasonYear}
            onChange={handleSeasonYearChange}
            disabled={isLoading}
            className="score-filter-select"
          >
            {seasonYearSelectOptions.map((year) => (
              <option key={year} value={String(year)}>
                {formatSeasonYearOptionLabel(year)}
              </option>
            ))}
          </select>
          <label htmlFor="score-tannery-filter" className="score-filter-label">
            Tannery
          </label>
          <select
            id="score-tannery-filter"
            value={selectedTanneryId}
            onChange={(event) => setSelectedTanneryId(event.target.value)}
            disabled={isLoading || !showTanneryFilter}
            className="score-filter-select"
            title={
              showTanneryFilter ? undefined : "Tannery filter applies to the live season only."
            }
          >
            {tanneryFilterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <label htmlFor="score-eid-day-filter" className="score-filter-label">
            Eid Day
          </label>
          <select
            id="score-eid-day-filter"
            value={selectedEidDay}
            onChange={(event) => setSelectedEidDay(event.target.value)}
            disabled={isLoading}
            className="score-filter-select"
          >
            {EID_DAY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <PrimaryButton onClick={loadScore}>{isLoading ? "Refreshing..." : "Refresh"}</PrimaryButton>
          <PrimaryButton type="button" onClick={openExportModal} disabled={isLoading}>
            Export
          </PrimaryButton>
        </div>
      </div>
      {errorMessage ? (
        <p className="dashboard-error-message" role="alert">
          {errorMessage}
        </p>
      ) : null}

      {isLoading ? <p>Loading score data...</p> : null}

      {isExportModalOpen ? (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setIsExportModalOpen(false)}>
          <div className="modal-content" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {isTanneryWiseExport(exportConfig.tannery_id)
                  ? "Export Tannery-wise Score"
                  : "Export Zila-wise Score"}
              </h2>
              <p className="dashboard-card-subtitle score-export-modal-note">
                {isTanneryWiseExport(exportConfig.tannery_id)
                  ? "Downloads a tannery report with zila breakdown per tannery, section totals, and prev-year comparison. Leave tannery as All Tanneries for the org-wide zila-wise export."
                  : "CSV and PDF include comparison totals, diff, and achieved % for the selected historical season. With all days selected, each day row compares to that same Eid day in the comparison year; each zila Total row compares to the full historical year for that zila. Select a tannery for a tannery-wise report."}
              </p>
              <button type="button" className="modal-close-btn" onClick={() => setIsExportModalOpen(false)}>
                x
              </button>
            </div>
            <div className="modal-form">
              {showTanneryFilter ? (
                <div className="form-group">
                  <label htmlFor="export-tannery">Tannery</label>
                  <select
                    id="export-tannery"
                    value={exportConfig.tannery_id}
                    onChange={(event) =>
                      setExportConfig((prev) => ({ ...prev, tannery_id: event.target.value }))
                    }
                  >
                    {tanneryFilterOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              <div className="form-group">
                <label htmlFor="export-eid-day">Eid Day</label>
                <select
                  id="export-eid-day"
                  value={exportConfig.eid_day}
                  onChange={(event) =>
                    setExportConfig((prev) => ({ ...prev, eid_day: event.target.value }))
                  }
                >
                  {EID_DAY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="export-language">Language</label>
                <select
                  id="export-language"
                  value={exportConfig.language}
                  onChange={(event) =>
                    setExportConfig((prev) => ({ ...prev, language: event.target.value }))
                  }
                >
                  <option value="en">English</option>
                  <option value="ur">Urdu</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="export-format">Format</label>
                <select
                  id="export-format"
                  value={exportConfig.format}
                  onChange={(event) =>
                    setExportConfig((prev) => ({ ...prev, format: event.target.value }))
                  }
                >
                  <option value="csv">CSV</option>
                  <option value="pdf">PDF</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <PrimaryButton type="button" onClick={() => setIsExportModalOpen(false)}>
                Cancel
              </PrimaryButton>
              <PrimaryButton
                type="button"
                onClick={() => handleExport(exportConfig)}
                disabled={Boolean(exportingKey)}
              >
                {exportingKey ? "Exporting..." : "Download"}
              </PrimaryButton>
            </div>
          </div>
        </div>
      ) : null}

      {!isLoading ? (
        <div className="score-tables-align">
          <section className="score-table-section">
            <h3>Total</h3>
            <ScoreDataTable
              metricColumns={metricColumns}
              tableKey="overall"
              rows={sortedOverallRows}
              labelHeader=""
              enableSort={false}
              emptyMessage="No total data found."
              getRowLabel={() =>
                EID_DAY_OPTIONS.find((option) => option.value === selectedEidDay)?.label ||
                "All Days"
              }
              renderSortLabel={renderSortLabel}
              onToggleSort={toggleSort}
            />
          </section>

          {showTanneryWiseTable ? (
            <section className="score-table-section">
              <h3>Tannery Wise</h3>
              <ScoreDataTable
                metricColumns={metricColumns}
                tableKey="tannery"
                rows={sortedTanneryRows}
                labelHeader=""
                showComparisonColumns={false}
                emptyMessage="No tannery-wise data found."
                getRowLabel={(row) => row.tannery_name || row.tanneryName || row.label}
                renderSortLabel={renderSortLabel}
                onToggleSort={toggleSort}
              />
            </section>
          ) : null}

          <section className="score-table-section">
            <h3>Zila Wise</h3>
            <ScoreDataTable
              metricColumns={metricColumns}
              tableKey="zila"
              rows={sortedZilaRows}
              showComparisonColumns={showZilaComparisons}
              labelHeader=""
              emptyMessage="No zila-wise data found."
              getRowLabel={(row) => row.zila || row.zila_name || row.district || row.label}
              renderSortLabel={renderSortLabel}
              onToggleSort={toggleSort}
            />
          </section>

          {showElaqaBreakdown ? (
            <section className="score-table-section">
              <h3>Elaqa Wise</h3>
              <ScoreDataTable
                metricColumns={metricColumns}
                tableKey="elaqa"
                rows={sortedElaqaRows}
                labelHeader=""
                showComparisonColumns={false}
                emptyMessage="No elaqa-wise data found."
                getRowLabel={(row) => row.elaqa || row.elaqa_name || row.area || row.label}
                renderSortLabel={renderSortLabel}
                onToggleSort={toggleSort}
              />
            </section>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

export default ScorePage;
